'use client';

import * as React from 'react';

import {
  ArrowRight,
  Laptop,
  LayoutGrid,
  LogOut,
  Moon,
  Plus,
  SunMedium
} from 'lucide-react';
import { signOut } from 'next-auth/react';
import { useRouter } from 'next-nprogress-bar';
import { useTheme } from 'next-themes';
import { usePathname } from 'next/navigation';

import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';
import { commandMenuConfig } from '@/config/command-menu';
import { useCommandMenu } from '@/context/command-menu-provider';
import { cn } from '@/lib/utils';

export function CommandMenu() {
  const pathname = usePathname();
  const router = useRouter();
  const { setTheme } = useTheme();
  const [open, setOpen] = React.useState(false);
  const { setIsSearching } = useCommandMenu();

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = React.useCallback((command: () => unknown) => {
    setOpen(false);
    command();
  }, []);

  const toggleTheme = React.useCallback(
    (theme: 'light' | 'dark' | 'system') => {
      setTheme(theme);
    },
    [setTheme]
  );

  return (
    <>
      <Button
        variant="outline"
        className={cn(
          'relative h-9 w-full justify-start rounded-[0.5rem] text-sm text-muted-foreground sm:pr-12 md:w-40 lg:w-64'
        )}
        onClick={() => setOpen(true)}
      >
        <span>Search...</span>
        <kbd className="pointer-events-none absolute right-1.5 top-2 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Actions">
            <CommandItem
              onSelect={() => runCommand(() => setIsSearching((prev) => !prev))}
            >
              <LayoutGrid className="mr-2 h-4 w-4" />
              Search Resources/Courses....
            </CommandItem>
            <CommandItem
              onSelect={() =>
                runCommand(() => router.push('/courses/link/new'))
              }
            >
              <Plus className="mr-2 h-4 w-4" />
              Create New Course...
            </CommandItem>
          </CommandGroup>
          <CommandGroup heading="Navigation">
            {commandMenuConfig.mainNav
              .filter((navitem) => !navitem.external)
              .map((navItem) => {
                const splitTitle = navItem.title.split(' ');
                return (
                  <CommandItem
                    key={navItem.href}
                    value={navItem.title}
                    onSelect={() => {
                      runCommand(() => router.push(navItem.href as string));
                    }}
                  >
                    <ArrowRight className="mr-2 h-4 w-4" />
                    <p>
                      <span>{splitTitle.slice(0, 2).join(' ')}</span>{' '}
                      <span className="font-semibold">
                        {splitTitle.length > 3
                          ? splitTitle.slice(2).join(' ')
                          : splitTitle.at(-1)}
                      </span>
                    </p>
                  </CommandItem>
                );
              })}
            {pathname.includes('/instructor') ? (
              <CommandItem
                value={'Switch to User Mode'}
                onSelect={() => {
                  runCommand(() => router.push('/dashboard'));
                }}
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                <p>
                  <span>Switch to</span>{' '}
                  <span className="font-semibold">User Mode</span>
                </p>
              </CommandItem>
            ) : (
              <CommandItem
                value={'Switch to Instructor Mode'}
                onSelect={() => {
                  runCommand(() => router.push('/instructor/dashboard'));
                }}
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                <p>
                  <span>Switch to</span>{' '}
                  <span className="font-semibold">Instructor Mode</span>
                </p>
              </CommandItem>
            )}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Theme">
            <CommandItem
              onSelect={() => runCommand(() => toggleTheme('light'))}
            >
              <SunMedium className="mr-2 h-4 w-4" />
              Light
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => toggleTheme('dark'))}>
              <Moon className="mr-2 h-4 w-4" />
              Dark
            </CommandItem>
            <CommandItem
              onSelect={() => runCommand(() => toggleTheme('system'))}
            >
              <Laptop className="mr-2 h-4 w-4" />
              System
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Quick actions">
            <CommandItem onSelect={() => runCommand(() => signOut())}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
